import React from 'react';
import styled from 'styled-components';

import appConfig from '../../../appConfig';
import useNotify from '../../../hooks/useNotify';
import { getRegisterId, greenIdVerification } from '../../../api/verification';
import { parseApiError } from '../../../utils/parseApiError';
import ReusableModal from '../../../components/ReusableModal/ReusableModal';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';

interface Props {
	onClose: () => void;
	onVerificationSuccess?: () => void;
	onVerificationFail?: (reason: string) => void;
}

declare global {
	interface Window {
		greenidJQuery: any;
		greenidUI: any;
	}
}

const GreenIdModal: React.FC<Props> = ({ onClose, onVerificationSuccess, onVerificationFail }) => {
	const { refreshPortalUser } = React.useContext(PortalSessionContext);
	const sending = React.useRef(false);
	const greenIdLoaded = React.useRef<boolean>(false);
	const [errors, setErrors] = React.useState<APIError>();
	const notify = useNotify();

	const onSessionCompleteCallback = React.useCallback(
		(verificationToken, overallState) => {
			if (overallState === 'VERIFIED' || overallState === 'VERIFIED_WITH_CHANGES') {
				greenIdVerification()
					.then(() => {
						refreshPortalUser();
						onVerificationSuccess && onVerificationSuccess();
						onClose();

						notify({
							type: 'success',
							title: 'Success',
							message: 'Your ID has been verified',
							timeout: 2000,
						});
					})
					.catch((error) => {
						setErrors(error.errors);
					})
					.finally(() => {
						sending.current = false;
					});
			} else {
				onVerificationFail && onVerificationFail(overallState);
				notify({
					type: 'warning',
					title: 'Verification Failed',
					message: 'We can’t verify your account, please contact customer Service',
					timeout: 2000,
				});
				onClose();
			}
		},
		[notify, onClose, refreshPortalUser, onVerificationFail, onVerificationSuccess]
	);

	const onSourceAttempt = React.useCallback((verificationToken, sourceName, checkState, overallState) => {
		//Todo: implement onSourceAttempt
	}, []);

	const onSessionCancel = React.useCallback(
		(verificationToken, overallState) => {
			console.log(overallState);
			if (overallState === overallState.CANCELLED) {
				onVerificationFail && onVerificationFail(overallState);
				notify({
					type: 'warning',
					title: 'Fail',
					message: overallState,
					timeout: 2000,
				});
				onClose();
			}
		},
		[notify, onClose, onVerificationFail]
	);

	const preSubmitValidationCallback = React.useCallback(() => {
		//Todo: implement preSubmitValidationCallback
		return true;
	}, []);

	const initGreenId = React.useCallback(() => {
		(async () => {
			if (window.greenidJQuery && !greenIdLoaded.current) {
				greenIdLoaded.current = true;
				try {
					const { data } = await getRegisterId();
					window.greenidJQuery('document').ready(() => {
						window.greenidUI.setup({
							environment: appConfig.greenId.env ?? 'test',
							frameId: 'greenid-div',
							country: 'usethiscountry',
							sessionCompleteCallback: onSessionCompleteCallback,
							sourceAttemptCallback: onSourceAttempt,
							sessionCancelledCallback: onSessionCancel,
							preSubmitValidationCallback: preSubmitValidationCallback,
						});
						window.greenidUI.show(appConfig.greenId.accountId, appConfig.greenId.apiCode, data.verification_token);
					});
				} catch (error: any) {
					if (error?.errors) setErrors(error.errors);
				}
			}
		})();
	}, [onSessionCancel, onSessionCompleteCallback, onSourceAttempt, preSubmitValidationCallback]);

	const loadGreenId = React.useCallback(() => {
		const greenIdCssUrl =
			appConfig.greenId.env === 'prod'
				? 'https://simpleui.edentiti.com/df/assets/stylesheets/greenid.css'
				: 'https://simpleui-test-au.vixverify.com/df/assets/stylesheets/greenid.css';

		const greenIdCss = document.createElement('link');
		greenIdCss.rel = 'stylesheet';
		greenIdCss.href = greenIdCssUrl;
		document.body.appendChild(greenIdCss);

		const greenIdConfigJsUrl =
			appConfig.greenId.env === 'prod'
				? 'https://simpleui.edentiti.com/df/javascripts/greenidConfig.js'
				: 'https://simpleui-test-au.vixverify.com/df/javascripts/greenidConfig.js';

		const greenIdConfigJs = document.createElement('script');
		greenIdConfigJs.src = greenIdConfigJsUrl;
		document.body.appendChild(greenIdConfigJs);

		const greenIdUIJsUrl =
			appConfig.greenId.env === 'prod'
				? 'https://simpleui.edentiti.com/df/javascripts/greenidui.min.js'
				: 'https://simpleui-test-au.vixverify.com/df/javascripts/greenidui.min.js';

		const greenIdUIJs = document.createElement('script');
		greenIdUIJs.onload = initGreenId;
		greenIdUIJs.src = greenIdUIJsUrl;
		document.body.appendChild(greenIdUIJs);
	}, [initGreenId]);

	React.useEffect(loadGreenId, [initGreenId, loadGreenId]);

	return (
		<ReusableModal onClose={onClose} width="800px" alignTop>
			<div id="greenid-div" />
			<ValidationError>{errors && parseApiError(errors)}</ValidationError>
		</ReusableModal>
	);
};

export default GreenIdModal;

const ValidationError = styled.p`
	color: ${({ theme }) => theme.colors.danger};
	max-width: 400px;
	margin: auto;
`;
